import { useEffect } from 'react';
import { analyticsAvailable, analyticsOn, track } from '../services/analytics';

/**
 * Reports how far down the landing a visitor got, as milestones (25, 50, 75 and 100
 * per cent of the page), each once per visit. Depth counts the bottom of the viewport,
 * so a visitor who sees the footer has reached 100. A passive, frame-throttled check;
 * it detaches once every milestone is in.
 *
 * PostHog's own pageleave event carries the maximum scroll too; milestones are what
 * funnels and trends can count directly.
 */

const MILESTONES = [25, 50, 75, 100] as const;

export function useScrollDepth(): void {
  useEffect(() => {
    if (!analyticsAvailable) return;
    let pending: number[] = [...MILESTONES];
    let frame = 0;

    const check = () => {
      frame = 0;
      // Before consent nothing is sent, so nothing is crossed off: a milestone passed
      // before the yes still counts the next time the visitor scrolls.
      if (!analyticsOn()) return;
      const doc = document.documentElement;
      const reached = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      const hit = pending.filter((m) => reached >= m - 0.5);
      if (hit.length === 0) return;
      hit.forEach((percent) =>
        track('landing_scroll_depth', { percent: percent as (typeof MILESTONES)[number] }),
      );
      pending = pending.filter((m) => !hit.includes(m));
      if (pending.length === 0) detach();
    };
    const onScroll = () => {
      frame ||= requestAnimationFrame(check);
    };
    const detach = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
    return detach;
  }, []);
}
